import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useWfWorkplaceStore } from 'src/api/wf/stores/WfWorkplaceStore';
import WF_Header from '../../common/header/WfHeader';
import WF_ListSearchHeader from '../../common/listSearchHeader/WF_ListSearchHeader';
import { getOperationStatusTranslationKey } from 'src/api/wf/models/enums/WfOperationtStatus';
import { useWfFilterStore } from 'src/api/wf/stores/WfFilterStore';
import useWfWorkplaceService from 'src/api/wf/service/WfWorkplaceService';
import { PaginationHeader } from '../../common/paginationHeader/PaginationHeader';
import { FormRow } from '../../common/form/FormRow';
import { useEffect, useState } from 'react';
import { LoadingSpinner } from '../../common/loading/LoadingSpinner';
import { useWfAlertHistoryStore } from 'src/api/wf/stores/WfAlertHistoryStore';
import { WfWorkplace } from 'src/api/wf/models/WfWorkplace';

function WF_4_4_2_PropertiesList() {
  const { t } = useTranslation();
  const { setSelectedWorkplace, selectedWorkplace, paginatedWorkplace, setPaginatedWorkplace } =
    useWfWorkplaceStore();
  const { patchFilterPagingParameters, filterPagingParameters, filterParameters } =
    useWfFilterStore();
  const { fetchPaginatedWorkplaces } = useWfWorkplaceService();
  const { patchHistorySearchParams } = useWfAlertHistoryStore();
  const [isSearched, setIsSearched] = useState(false);

  const [isLoading, setIsLoading] = useState(false);
  const [isTimeout, setIsTimeout] = useState(false);

  useEffect(() => {
    setSelectedWorkplace(null);

    const hasMandatoryFilters =
      Boolean(filterParameters.branch) && Boolean(filterParameters.salesOffice);
    const hasCustomerFilter = Boolean(filterParameters.customerNumber);

    if ((hasMandatoryFilters || hasCustomerFilter) && !isSearched) {
      handleSearch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSelectWorkplace = (site: WfWorkplace | null) => {
    setSelectedWorkplace(site);
    if (site) {
      patchHistorySearchParams({
        customerNumber: site.customerId,
        branchCode: site.branchCode,
        salesOfficeCode: site.salesOfficeCode,
        organizationCode: site.organizationCode,
        buildingName: site.name,
      });
    } else {
      patchHistorySearchParams({
        customerNumber: null,
        branchCode: null,
        salesOfficeCode: null,
        organizationCode: null,
        buildingName: null,
      });
    }
  };

  const handlePageChange = async (newPage: number) => {
    setIsLoading(true);
    setIsTimeout(false);
    setPaginatedWorkplace(null);
    setSelectedWorkplace(null);
    patchFilterPagingParameters({ pageNumber: newPage - 1 });
    try {
      await fetchPaginatedWorkplaces(newPage - 1);
    } catch (error) {
      if (axios.isAxiosError(error) && error.code === 'ECONNABORTED') {
        setIsTimeout(true);
      } else {
        console.error(error);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = async () => {
    setIsLoading(true);
    setIsTimeout(false);
    setPaginatedWorkplace(null);
    setSelectedWorkplace(null);
    patchFilterPagingParameters({ pageNumber: 0 });
    try {
      await fetchPaginatedWorkplaces(0);
    } catch (error) {
      if (axios.isAxiosError(error) && error.code === 'ECONNABORTED') {
        setIsTimeout(true);
      } else {
        console.error(error);
      }
    } finally {
      // タイムアウトしても結果・タイムアウト表示のセクションを必ず描画する
      setIsSearched(true);
      setIsLoading(false);
    }
  };

  return (
    <div className="bg-gray-100">
      <WF_Header />
      <WF_ListSearchHeader onSearch={handleSearch} />

      {isLoading && <LoadingSpinner />}

      {!isLoading && isSearched && (
        <>
          {paginatedWorkplace?.pageElements && paginatedWorkplace?.pageElements.length > 0 && (
            <PaginationHeader
              page={filterPagingParameters?.pageNumber + 1}
              perPage={paginatedWorkplace?.pageSize}
              total={paginatedWorkplace?.totalElements ?? 0}
              onPageChange={(p) => handlePageChange(p)}
            />
          )}

          {paginatedWorkplace?.pageElements.map((site) => {
            const isSelected = site.customerId === selectedWorkplace?.customerId;
            return (
              <div
                key={site.customerId}
                className={`mt-4 flex gap-2 border-t border-gray-300 px-4 ${isSelected ? 'bg-sky-200' : ''}`}
              >
                <div className="py-2">
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => handleSelectWorkplace(isSelected ? null : site)}
                  />
                </div>
                <div className="flex flex-col gap-4 py-2">
                  <FormRow title={t('wf.common.customerNumber')}>
                    <span>{site.customerId}</span>
                  </FormRow>
                  <FormRow title={t('wf.propertyList.status')}>
                    <span>{t(getOperationStatusTranslationKey(site.details.statusId))}</span>
                  </FormRow>
                  <FormRow title={t('wf.propertyList.buildingName')}>
                    <span>{`${site.emergencyClass ? '★ ' : ''}${site.name}`}</span>
                  </FormRow>
                  {site.details.reservations.length !== 0 && (
                    <FormRow title={t('wf.propertyList.reservedEngineer')}>
                      <span>{site.details.reservations[0].reserverName}</span>
                    </FormRow>
                  )}
                  {site.details.reservations.length !== 0 && (
                    <FormRow title={t('wf.propertyList.assignedEngineer')}>
                      <span>{site.details.reservations[0].engineerName}</span>
                    </FormRow>
                  )}
                </div>
              </div>
            );
          })}

          {(!paginatedWorkplace?.pageElements || paginatedWorkplace.pageElements.length === 0) && (
            <div className="mt-4 px-4 text-center">
              {t(isTimeout ? 'wf.common.searchTimeout' : 'wf.common.noResults')}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default WF_4_4_2_PropertiesList;
